import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Icon } from 'semantic-ui-react';
import './ConfirmModal.css';

/**
 * ConfirmModal — Reusable confirmation modal matching the app's custom modal style.
 * Uses a portal-based overlay instead of Semantic UI Modal.
 *
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Called when backdrop or cancel is clicked
 * @param {string} title - Modal title text
 * @param {string} titleIconName - Semantic UI icon name for the header (e.g. 'trash')
 * @param {string} titleIconColor - Semantic UI icon color (e.g. 'red', 'orange')
 * @param {React.ReactNode} children - Body content
 * @param {string} confirmText - Confirm button label
 * @param {string} cancelText - Cancel button label
 * @param {function} onConfirm - Called when confirm button is clicked
 * @param {string} confirmIconName - Semantic UI icon name for confirm button (e.g. 'trash')
 * @param {boolean} confirmDisabled - Disable the confirm button
 * @param {boolean} isLoading - Show loading state on confirm button
 * @param {string} loadingText - Text shown while loading
 * @param {string} variant - 'danger' | 'warning' | 'default'
 * @param {boolean} closeOnOverlay - Allow closing by clicking overlay (default true)
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  title,
  titleIconName = 'trash',
  titleIconColor,
  children,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  confirmIconName,
  confirmDisabled = false,
  isLoading = false,
  loadingText = 'Processing...',
  variant = 'danger',
  closeOnOverlay = true
}) => {
  const overlayRef = useRef(null);

  // Derive icon color from variant if not explicitly provided
  const resolvedIconColor = titleIconColor || (
    variant === 'danger' ? 'red' :
    variant === 'warning' ? 'orange' :
    'teal'
  );

  // Derive confirm button icon from variant if not explicitly provided
  const resolvedConfirmIcon = confirmIconName || (
    variant === 'danger' ? 'trash' :
    variant === 'warning' ? 'warning sign' :
    'check'
  );

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape' && !isLoading && closeOnOverlay) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, isLoading, onClose, closeOnOverlay]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current && closeOnOverlay && !isLoading) {
      onClose();
    }
  };

  const confirmBtnClass = `confirm-modal-btn confirm-modal-btn--${variant}`;

  const modalContent = (
    <div
      className="confirm-modal-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
    >
      <div className="confirm-modal">
        {/* Loading overlay */}
        {isLoading && (
          <div className="confirm-modal-loading">
            <div className="confirm-modal-spinner" />
            <span>{loadingText}</span>
          </div>
        )}

        {/* Header */}
        <div className="confirm-modal-header">
          <span className="confirm-modal-title">
            <Icon name={titleIconName} color={resolvedIconColor} style={{ margin: 0 }} />
            {title}
          </span>
        </div>

        {/* Body */}
        <div className="confirm-modal-body">
          {children}
        </div>

        {/* Actions */}
        <div className="confirm-modal-actions">
          <button
            className="confirm-modal-btn confirm-modal-btn--cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </button>
          {onConfirm && (
            <button
              className={confirmBtnClass}
              onClick={onConfirm}
              disabled={confirmDisabled || isLoading}
            >
              {isLoading ? (
                <>
                  <span className="confirm-modal-btn-spinner" />
                  {loadingText}
                </>
              ) : (
                <>
                  <Icon name={resolvedConfirmIcon} style={{ margin: 0 }} />
                  {confirmText}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ConfirmModal;
